import React, { useEffect } from 'react';
import { Card, Skeleton, App as AntdApp } from 'antd';
import { trpc } from '../../lib/trpc';
import SubscriptionTab from '../../components/company/SubscriptionTab';
import { PageHeader, PageContainer, DevFilePathBadge } from '../../components/common';
import { HelpButton } from '../../components/help/HelpButton';

const SubscriptionSettingsPage: React.FC = () => {
  const { message: messageApi } = AntdApp.useApp();
  const utils = trpc.useUtils();
  const { data: company, isLoading, error } = trpc.company.getCurrentCompany.useQuery();
  const { data: currentUser } = trpc.user.getProfile.useQuery();
  const isManager = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  const finalizeCheckoutMutation = trpc.stripe.finalizeCheckoutSession.useMutation({
    onSuccess: async () => {
      messageApi.success('Stripe契約情報を同期しました。');
      await utils.company.getCurrentCompany.invalidate();
    },
    onError: (error) => {
      messageApi.error(error?.message ?? 'Stripe契約情報の同期に失敗しました。');
    },
  });
  const finalizeCheckout = finalizeCheckoutMutation.mutateAsync;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get('stripeCheckout');
    if (!checkoutResult) {
      return;
    }

    const clearUrl = () => {
      window.history.replaceState({}, document.title, window.location.pathname);
    };

    if (checkoutResult === 'success') {
      const sessionId = params.get('session_id');
      if (!sessionId) {
        messageApi.warning('StripeのセッションIDが取得できませんでした。Stripeのイベントログを確認してください。');
        clearUrl();
        return;
      }

      const hide = messageApi.loading('Stripe契約情報を同期中です...', 0);
      void finalizeCheckout({ sessionId })
        .catch(() => {
          // エラー表示は useMutation の onError で処理済み
        })
        .finally(() => {
          hide();
          clearUrl();
        });
    } else if (checkoutResult === 'cancel') {
      messageApi.info('Stripeでのサブスク契約はキャンセルされました。');
      clearUrl();
    }
  }, [finalizeCheckout, messageApi]);

  useEffect(() => {
    if (!error) {
      return;
    }
    messageApi.error('会社情報の取得に失敗しました');
  }, [error, messageApi]);

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          title="契約プラン"
          subtitle="契約プランの確認・変更、支払い方法の管理を行います"
        />
        <Card>
          <Skeleton active />
        </Card>
      </PageContainer>
    );
  }

  if (!isManager) {
    return (
      <PageContainer>
        <PageHeader
          title="契約プラン"
          subtitle="契約プランの確認・変更、支払い方法の管理を行います"
        />
        <Card>
          <p>この機能を利用するには管理者権限が必要です。</p>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DevFilePathBadge filePath="client/src/pages/settings/SubscriptionSettingsPage.tsx" />
      <PageHeader
        title="契約プラン"
        subtitle="契約プランの確認・変更、支払い方法の管理を行います"
        extra={<HelpButton topicId="subscription-overview" size="middle" position="inline" />}
      />
      <Card>
        <SubscriptionTab company={company} isManager={isManager} currentUser={currentUser ?? undefined} />
      </Card>
    </PageContainer>
  );
};

export default SubscriptionSettingsPage;
